import React from "react";
import toast from "react-hot-toast";
import ThemedModal from "../../utilities/ThemedModal";
import LoadingMessage from "../../loaders/LoadingMessage";
import { Controller, useForm } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useMasterAccountLookup, useMasterAccountLookupProps } from "../../../hooks/useMasterAccountLookup";
import { useFileMailMutation, useReFileMailMutation } from "../../../services/useremails";
import { Modal } from "react-bootstrap";
import { Button } from "../../";
import { MailMessageControlOptions } from "../../../hooks/useMailMessageControls";
import { ClientProjectSearchSelect, MasterAccountSelect } from "../../forms/selects";
import { FileEmailModalForm, FileEmailModalModel, FileEmailModalProps } from "./types";
import { MailMessageData } from "../../../types/MailMessageData";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";

// TODO: there is a tiny delay in displaying the master account data in the select box - I'm not happy with it but it will have to do for now

type FileMailMasterAccount = {
    masterAccountId: string
    divisionId: string
};

type FileMailResult = {
    data: MailMessageData
}

const FileMailModal = ({
    additionalQueryParams,
    backdrop = 'static',
    cancel,
    centered = true,
    defaultValues,
    delegate,
    dialogId,
    dismiss,
    isFiled,
    proceed,
    show,
    size = 'lg',
    ...rest
}: Readonly<FileEmailModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const [fileMailTrigger, fileMailResult] = useFileMailMutation();
    const [reFileMailTrigger, reFileMailResult] = useReFileMailMutation();
    const { control, formState: { errors }, getFieldState, handleSubmit, reset, setValue, watch } = useForm<FileEmailModalForm>({
        defaultValues: {
            masterAccountId: defaultValues?.masterAccountId ?? null,
            projectId: defaultValues?.projectId ?? null
        }
    });

    const defaultSearchFilters : useMasterAccountLookupProps = defaultValues?.masterAccountId
        ? { accountId: defaultValues?.masterAccountId }
        : { emailAddress: defaultValues?.fromEmail };

    const { data, error, isBusy: isBusyDoingLookup, lookedUpById } = useMasterAccountLookup(defaultSearchFilters);
    const [selectedMasterAccount, setSelectedMasterAccount] = React.useState<SelectOptionData<string, FileMailMasterAccount> | null >(null);
    const [isFormLoading] = useDebounce(isBusyDoingLookup, 300);
    const masterAccountId = watch('masterAccountId');
    const isBusy = false;
    const fileButtonText = isFiled ? 'Re-File Email' : 'File Email';
    const fileButtonVariant = isFiled ? 'warning' : 'success';

    const onClose = () : void => {
        dismiss!();
    };

    const onSubmit = (form: FileEmailModalForm) : void => {
        if (!defaultValues) {
            toast.error(`Unable to file email. Incomplete data supplied.`);
            dismiss!();
            return;
        }

        const queryParams = additionalQueryParams ?? { folderId: null, searchTerm: null };
        if (defaultValues.isFiled || defaultValues.masterAccountId || defaultValues.projectId) {
            toast.promise(reFileMailTrigger({
                mailId: defaultValues.id,
                request: form,
                ...queryParams
            }), {
                loading: "Updating filed email...",
                error: (err) => err?.data?.message ?? "Unable to update filed email. Please try again later.",
                success: (res: FileMailResult) => {
                    proceed!(res.data);
                    return `Filed email has been updated.`
                }
            }).then(() => {});
        }
        else {
            toast.promise(fileMailTrigger({
                mailId: defaultValues.id,
                userId: delegate?.userId ?? lookedUpById,
                request: form,
                ...queryParams
            }), {
                loading: "Filing mail...",
                error: (err) => err?.data?.message ?? "Unable to file mail.",
                success: (res: FileMailResult) => {
                    proceed!(res.data);
                    return `Email has been filed.`
                }
            }).then(() => {});
        }
    };

    React.useEffect(() => {
        if (!data) {
            return;
        }

        const currentState = getFieldState('masterAccountId');
        if (currentState.isDirty === false) {
            setSelectedMasterAccount(() => ({
                label: data.accountName,
                value: data.masterAccountId,
                data: data
            }));
        }
    }, [ data ]);

    React.useEffect(() => {
        if (!selectedMasterAccount) {
            return;
        }
        if (!masterAccountId || masterAccountId !== selectedMasterAccount.value) {
            setValue('projectId', null, { shouldDirty: true });
            setValue('masterAccountId', selectedMasterAccount.value, { shouldDirty: true });
        }
    }, [ selectedMasterAccount ]);

    return (
        <Modal className="overflow-visible" backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>
                    {fileButtonText}
                </Modal.Title>
            </ThemedModal.Header>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Modal.Body ref={bodyAnimation}>
                    {isFormLoading && (
                        <LoadingMessage text="Checking the sender against our records" />
                    )}
                    {!isFormLoading && (
                        <div className="row gy-2">
                            <div className="col-12">
                                <Controller
                                    name="masterAccountId"
                                    control={control}
                                    rules={{ required: "Master account is required." }}
                                    render={({ field: { ref, value, onChange, onBlur, ...rest } }) => (
                                        <MasterAccountSelect
                                            disableAnimations={true}
                                            errorMessage={errors?.masterAccountId?.message}
                                            valueObject={selectedMasterAccount}
                                            onChange={(opt: SelectOptionData<string, { masterAccountId: string, divisionId: string }>) => {
                                                setSelectedMasterAccount(() => opt);
                                            }}
                                            {...rest}
                                        />
                                    )}
                                />
                            </div>
                            {selectedMasterAccount && (
                                <div className="col-12">
                                    <Controller
                                        name="projectId"
                                        control={control}
                                        render={({ field: { ref, value, onChange, onBlur, ...rest } }) => (
                                            <ClientProjectSearchSelect
                                                allowCreate={false}
                                                disableAnimations={true}
                                                isClearable={true}
                                                errorMessage={errors?.projectId?.message}
                                                masterAccountId={selectedMasterAccount?.data!.masterAccountId}
                                                divisionId={selectedMasterAccount?.data!.divisionId}
                                                userId={lookedUpById}
                                                value={value}
                                                onChange={(opt: SelectOption<string>) => {
                                                    if (!opt) {
                                                        setValue('projectId', null, { shouldDirty: true });
                                                        return;
                                                    }
                                                    setValue('projectId', opt.value, { shouldDirty: true });
                                                }}
                                                {...rest}
                                            />
                                        )}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button type="submit" variant={fileButtonVariant} disabled={isBusy}>{fileButtonText}</Button>
                    <Button type="button" variant="light" disabled={isBusy} onClick={onClose}>Cancel</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export function openFileEmailModal(mailMessage: FileEmailModalModel, delegate: UserDelegate | null, opts: MailMessageControlOptions | null = null) : Promise<MailMessageData> {
    return createConfirmation<FileEmailModalProps, MailMessageData>(confirmable(FileMailModal))({
        additionalQueryParams: opts,
        dialogId: 'email_file_modal',
        defaultValues: mailMessage,
        delegate,
        isFiled: !!(mailMessage?.isFiled && mailMessage?.isAssignedToProject)
    });
}